import {
  AddOutlined,
} from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';

import { OrganizationViewModel, useDeleteOrganization, useGetOrganizations, useGetVerticals } from '@/api';
import { DeletePopup, PageHeaderLayout, tableTypes, UITable } from '@/shared/components';
import { useMedia } from '@/shared/hooks';

import { CreateEditModal } from './components';
import { organizationsColumns } from './duck';

const OrganizePage: React.FC = () => {
  const { isSxScreen } = useMedia();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteOrganization, isDeleting } = useDeleteOrganization();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openCreateEditModal, setOpenCreateEditModal] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedOrganization, setSelectedOrganization] = React.useState<OrganizationViewModel>();

  const [sortConfig, setSortConfig] = React.useState<{
    sortBy: 'name' | 'domain';
    sortOrder: 'asc' | 'desc';
  }>({
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleSort = (columnKey: string) => {
    if (columnKey === 'name' || columnKey === 'domain') {
      setSortConfig(prev => (
        {
          sortBy: columnKey,
          sortOrder: prev.sortBy === columnKey && prev.sortOrder === 'asc' ? 'desc' : 'asc',
        }
      ));
    }
  };

  const {
    organizations,
    totalPages,
    totalItems,
    isOrganizationsLoading,
  } = useGetOrganizations({
    page: currentPage + 1,
    pageSize: rowsPerPage,
    sortBy: sortConfig.sortBy,
    sortOrder: sortConfig.sortOrder,
  });

  const {
    verticalsList,
    isVerticalsListLoading,
  } = useGetVerticals();

  const handleDeleteOrganization = async () => {
    try {
      if (selectedOrganization) {
        await deleteOrganization(selectedOrganization.id);
        enqueueSnackbar({
          message: 'Organization deleted successfully!',
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar({
        message: error instanceof Error ? error.message : 'An error occurred while deleting the organization.',
        variant: 'error',
      });
    }

    setSelectedOrganization(undefined);
  };

  const tableConfig: tableTypes.MuiTableProps<OrganizationViewModel> = {
    data: organizations,
    columns: organizationsColumns(verticalsList),
    loading: isOrganizationsLoading || isDeleting || isVerticalsListLoading,
    itemActions: item => (
      {
        itemList: [
          {
            title: 'Edit',
            itemAction: () => {
              setSelectedOrganization(item);
              setOpenCreateEditModal(true);
            },
          },
          {
            title: 'Delete',
            itemAction: () => {
              setSelectedOrganization(item);
              setOpenDeleteModal(true);
            },
            isRedText: true,
          },
        ],
      }
    ),
    onSort: handleSort,
    sortConfig: sortConfig,
    pagination: {
      count: totalItems,
      totalPages: totalPages,
      rowsPerPage: rowsPerPage,
      page: currentPage,
      onPageChange: handleChangePage,
      onRowsPerPageChange: handleChangeRowsPerPage,
    },
    emptyContentTitle: 'No organizations found',
    emptyContentSubTitle: 'Click the "Create Organization" button to start your first evaluation.',
  };

  return (
    <Stack sx={{ height: '100%' }} gap={isSxScreen ? '1rem' : ''}>
      <PageHeaderLayout headerName='Organizations'>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddOutlined />}
          fullWidth={isSxScreen}
          onClick={() => setOpenCreateEditModal(true)}
        >
          {'Create Organization'}
        </Button>
      </PageHeaderLayout>

      <UITable {...tableConfig} />

      {openCreateEditModal && (
        <CreateEditModal
          isOpen={openCreateEditModal}
          onClose={() => setOpenCreateEditModal(false)}
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
      )}

      {openDeleteModal && (
        <DeletePopup
          keyId='delete-organization'
          title='Delete Organization'
          itemName={selectedOrganization?.name}
          isOpen={openDeleteModal}
          onConfirm={handleDeleteOrganization}
          onClose={() => setOpenDeleteModal(false)}
        />
      )}
    </Stack>
  );
};

export default OrganizePage;